/**
 * AppSidebar — collapsible sidebar for the (app) layout.
 *
 * M1.4: Full nav items wired up with NavLink active-state styling.
 *   - Dashboard  (LayoutDashboard icon)
 *   - Parties    (Users icon)
 *   - Batches    (GraduationCap icon)
 *   - Logout     (LogOut icon)
 *
 * Collapse state is persisted via Zustand (localStorage-backed per ui-store.ts).
 * Accessibility: nav is <nav aria-label="Main navigation">, items are <ul>/<li>/<NavLink>.
 */

import { Archive, BarChart3, Bell, CalendarCheck, CalendarOff, CalendarPlus, ClipboardCheck, Download, FileBarChart, FileText, HelpCircle, IndianRupee, Landmark, LayoutDashboard, ListChecks, LogOut, Mail, PanelLeftClose, PanelLeftOpen, Plug, ReceiptText, ScanLine, ScrollText, Shield, ShieldAlert, ShieldCheck, Upload, UserCheck, UserCog, UserPlus, Users, type LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { TALLY_MANUAL_MODE } from '@/lib/constants';
import { useUIStore } from '@/stores/ui-store';
import { useLogoutMutation } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth-store';

// ─── Nav item definition ──────────────────────────────────────────────────────

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  // end=true means NavLink only marks active on exact match (not prefix)
  end?: boolean;
  // section — when set, a labelled divider is rendered above this item.
  // The divider carries the section icon when the sidebar is not collapsed.
  section?: { label: string; icon: LucideIcon };
  /**
   * Optional permission string — when set, the item is only rendered if the
   * current user has this permission in their permissions array.
   * RBAC enforcement note: hiding a sidebar item is PRESENTATION ONLY.
   * The route still loads if directly visited; the page component and the
   * backend API both enforce access independently.
   */
  requirePermission?: string;
  /**
   * Optional OR-list — the item is shown if the user holds ANY of these
   * permissions. Use for areas reachable by several permissions (e.g. the
   * approval queue, which any approval layer can access).
   */
  requireAnyPermission?: string[];
}

const APPROVAL_PERMS = [
  'invoice.approve',
  'invoice.approve.l1',
  'invoice.approve.l2',
  'invoice.approve.l3',
  'invoice.approve.l4',
  'invoice.approve.l5',
];

export const NAV_ITEMS: NavItem[] = [
  // Dashboard is the only always-visible item — every authenticated user has one.
  { to: '/dashboard',  label: 'Dashboard',          icon: LayoutDashboard,  end: true },
  // My Task Manager — the task-centric home that unifies the batch + invoice flow.
  { to: '/tasks',      label: 'My Task Manager',    icon: ListChecks },
  { to: '/my-leave',   label: 'My Leave',           icon: CalendarPlus,     requirePermission: 'leave.submit' },
  // Approvals — the approver's cross-batch queue (officer home), promoted to top level.
  { to: '/approvals',  label: 'Approvals',          icon: ClipboardCheck,   requireAnyPermission: APPROVAL_PERMS },
  { to: '/approvals/bypass', label: 'Bypass Approvals', icon: ShieldAlert,  requirePermission: 'invoice.bypass_authorize' },
  { to: '/leave-approvals', label: 'Leave Approvals', icon: CalendarCheck,  requirePermission: 'leave.approve' },
  { to: '/parties',    label: 'Parties',            icon: Users,            requirePermission: 'party.read' },

  // NOTE: the former "Advanced" per-domain pages (Batches / Receipts / Payment
  // Vouchers / Checks / Invoices) are DEPRECATED — every batch's data + actions
  // now live inside the My Task Manager workflow steps, with read-only detail in
  // modals (invoice / BPV). Their routes remain for deep-links. Tally ledger
  // config moved to the Settings section below.

  // ── GST Compliance section ────────────────────────────────────────────────
  {
    to: '/gst/holds',
    label: 'GST Holds',
    icon: Shield,
    section: { label: 'GST Compliance', icon: Shield },
    requirePermission: 'gst.read',
  },
  { to: '/gst/2b-uploads',             label: 'GSTR-2B Uploads', icon: Upload,      requirePermission: 'gst.read' },
  {
    to: '/gst/2b-automated',
    label: '2B Automated Download',
    icon: Download,
    requirePermission: 'gst.upload_2b',
  },
  { to: '/gst/non-compliance-report',  label: 'GST Report',      icon: FileBarChart,    requirePermission: 'gst.read' },
  { to: '/gst/pending-payments',       label: 'Pending GST Payments', icon: IndianRupee, requirePermission: 'gst.read' },

  // ── Reports section (M4.3) ───────────────────────────────────────────────
  {
    to: '/reports',
    label: 'Reports',
    icon: BarChart3,
    section: { label: 'Reports', icon: BarChart3 },
    requirePermission: 'report.read',
  },

  // ── Settings section ─────────────────────────────────────────────────────
  {
    to: '/notifications',
    label: 'Notifications',
    icon: Bell,
    section: { label: 'Settings', icon: Bell },
    // All authenticated users — no requirePermission.
  },
  {
    to: '/bank-templates',
    label: 'Bank Templates',
    icon: Landmark,
    requirePermission: 'check.template.read',
  },
  {
    to: '/tally-vouchers',
    label: 'Tally Ledgers',
    icon: ReceiptText,
    requirePermission: 'bpv.post_to_tally',
  },

  // ── Administration section (admin-only items) ─────────────────────────────
  {
    to: '/registrations',
    label: 'Registration Requests',
    icon: UserPlus,
    section: { label: 'Administration', icon: UserCog },
    requirePermission: 'registration.review',
  },
  {
    to: '/settings/registration-approvers',
    label: 'Registration Approvers',
    icon: UserCheck,
    requirePermission: 'registration.manage_approvers',
  },
  {
    to: '/users',
    label: 'Users',
    icon: UserCog,
    requirePermission: 'user.read',
  },
  {
    to: '/roles',
    label: 'Roles & Permissions',
    icon: ShieldCheck,
    requirePermission: 'role.read',
  },
  {
    to: '/leave',
    label: 'Leave Management',
    icon: CalendarOff,
    requirePermission: 'leave.read',
  },
  {
    to: '/archived-batches',
    label: 'Archived Batches',
    icon: Archive,
    requirePermission: 'batch.archive',
  },
  {
    to: '/party-review',
    label: 'Party Review',
    icon: ShieldAlert,
    requirePermission: 'party.review.read',
  },
  {
    to: '/faculty-fee-rates',
    label: 'Faculty Fee Rates',
    icon: IndianRupee,
    requirePermission: 'faculty_fee.manage',
  },
  {
    to: '/vendor-fee-rates',
    label: 'Vendor Fee Rates',
    icon: IndianRupee,
    requirePermission: 'vendor_fee.manage',
  },
  {
    to: '/email-logs',
    label: 'Email Logs',
    icon: Mail,
    requirePermission: 'email_log.read',
  },
  {
    to: '/email-templates',
    label: 'Email Templates',
    icon: FileText,
    requirePermission: 'email_template.manage',
  },
  {
    to: '/settings/email',
    label: 'Email Settings',
    icon: Mail,
    requirePermission: 'email_settings.manage',
  },
  {
    to: '/settings/tally-connector',
    label: 'Tally Connector',
    icon: Plug,
    requirePermission: 'tally_connector.manage',
  },
  {
    to: '/settings/invoice-extractor',
    label: 'AI Invoice Extractor',
    icon: ScanLine,
    requirePermission: 'invoice_extractor.manage',
  },
  {
    to: '/audit-log',
    label: 'Audit Log',
    icon: ScrollText,
    requirePermission: 'audit_log.read',
  },
  // Help & Guides — ungated; available to every authenticated user.
  {
    to: '/help',
    label: 'Help & Guides',
    icon: HelpCircle,
    section: { label: 'Support', icon: HelpCircle },
  },
];

/**
 * Filter nav items by permission gate — shared by the sidebar and the ⌘K
 * command palette so both surface exactly the same set of pages.
 * Items without requirePermission are always shown; gated items are hidden when
 * the user lacks the permission. Admin roles carry the wildcard '*'.
 * RBAC note: presentation-only — the page and backend still enforce access.
 */
export function filterVisibleNavItems(permissions: string[]): NavItem[] {
  const hasWildcard = permissions.includes('*');
  return NAV_ITEMS.filter((item) => {
    // Manual Tally mode: hide the connector settings page across the app.
    if (TALLY_MANUAL_MODE && item.to === '/settings/tally-connector') return false;
    if (hasWildcard) return true;
    if (item.requireAnyPermission) {
      return item.requireAnyPermission.some((p) => permissions.includes(p));
    }
    if (item.requirePermission) {
      return permissions.includes(item.requirePermission);
    }
    return true; // ungated items (Dashboard, Notifications) are shown to everyone
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AppSidebar() {
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const logoutMutation = useLogoutMutation();
  const currentUser = useAuthStore((s) => s.user);
  const permissions = currentUser?.permissions ?? [];

  const visibleItems = filterVisibleNavItems(permissions);

  return (
    <aside
      className={cn(
        'flex flex-col border-r border-border bg-card transition-all duration-200 shrink-0',
        sidebarCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo / wordmark + collapse toggle */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-border">
        {!sidebarCollapsed && (
          <span className="font-bold text-primary text-sm tracking-tight truncate">NIRC SCO</span>
        )}
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
      </div>

      {/* Main navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2" aria-label="Main navigation">
        <ul className="flex flex-col gap-0.5 list-none m-0 p-0">
          {visibleItems.map(({ to, label, icon: Icon, end, section }) => (
            <li key={to}>
              {/* Section divider — rendered above the first item of a new section. */}
              {section && (
                <div
                  className={cn(
                    'flex items-center gap-2 mt-4 mb-1 px-3',
                    sidebarCollapsed ? 'justify-center' : ''
                  )}
                  aria-hidden="true"
                >
                  <section.icon
                    size={14}
                    className="shrink-0 text-muted-foreground"
                  />
                  {!sidebarCollapsed && (
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground truncate">
                      {section.label}
                    </span>
                  )}
                </div>
              )}

              <NavLink
                to={to}
                end={end}
                aria-label={sidebarCollapsed ? label : undefined}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      size={18}
                      className={cn(
                        'shrink-0',
                        isActive ? 'text-primary-foreground' : 'text-muted-foreground'
                      )}
                    />
                    {!sidebarCollapsed && <span className="truncate">{label}</span>}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom — logout */}
      <div className="p-2 border-t border-border">
        <button
          type="button"
          onClick={() => logoutMutation.mutate()}
          disabled={logoutMutation.isPending}
          aria-label="Sign out"
          className={cn(
            'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
            'text-muted-foreground hover:text-destructive hover:bg-destructive/10',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:opacity-60 disabled:cursor-not-allowed'
          )}
        >
          <LogOut size={18} className="shrink-0" />
          {!sidebarCollapsed && (
            <span className="truncate">
              {logoutMutation.isPending ? 'Signing out…' : 'Sign out'}
            </span>
          )}
        </button>
      </div>
    </aside>
  );
}
